import React, { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { Flag, Zap } from 'lucide-react';
import { Square } from '../types';

interface GameSquareProps {
  square: Square;
  gameOver: boolean;
  onSquareClick: () => void;
  onLongPress: () => void;
  onLongPressEnd: () => void;
  onRightClick: () => void;
}

const GameSquare: React.FC<GameSquareProps> = ({
  square,
  gameOver,
  onSquareClick,
  onLongPress,
  onLongPressEnd,
  onRightClick,
}) => {
  const getSquareContent = (): React.ReactNode => {
    if (square.flagged && !gameOver) {
      return (
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          exit={{ scale: 0, rotate: 180 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
        >
          <Flag size={18} color="#e53e3e" fill="#e53e3e" />
        </motion.div>
      );
    }
    
    if (square.mine && gameOver) {
      return (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.2, 1] }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Zap size={16} color="#ffd700" fill="#ffd700" />
        </motion.div>
      );
    }
    
    if (square.clicked && !square.mine && square.proximityTotal > 0) {
      return (
        <motion.span
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          style={{ 
            fontWeight: 'bold', 
            fontSize: '14px',
            textShadow: '0 1px 2px rgba(0,0,0,0.2)'
          }}
        >
          {square.proximityTotal}
        </motion.span>
      );
    }
    
    return '';
  };

  const getSquareColor = (): string => {
    // Handle flagged squares first (regardless of whether it's a mine)
    if (square.flagged && !gameOver) return '#dc3545'; // Bright red for better contrast
    
    // Handle mines that are revealed during game over
    if (square.mine && gameOver) return 'white';
    
    // Handle proximity numbers
    switch (square.proximityTotal) {
      case 1: return '#007bff'; // Modern blue
      case 2: return '#28a745'; // Modern green
      case 3: return '#dc3545'; // Modern red
      case 4: return '#6f42c1'; // Modern purple
      case 5: return '#fd7e14'; // Modern orange
      case 6: return '#20c997'; // Modern teal
      case 7: return '#343a40'; // Modern dark gray
      case 8: return '#6c757d'; // Modern gray
      default: return '#6c757d';
    }
  };

  const getBackgroundColor = (): string => {
    if (square.mine && (square.clicked || gameOver)) {
      return 'red';
    }
    if (square.clicked && square.proximityTotal === 0) {
      return '#e6e6e6';
    }
    return 'white';
  };

  const squareStyle: CSSProperties = {
    textAlign: 'center',
    background: getBackgroundColor(),
    cursor: gameOver ? 'default' : 'pointer',
    position: 'relative',
    width: '40px',
    height: '40px',
    border: '1px solid #e0e6ed',
    fontWeight: '700',
    fontSize: '15px',
    color: getSquareColor(),
    userSelect: 'none',
    borderRadius: '3px',
  };

  const contentStyle: CSSProperties = {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    margin: 'auto',
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    // Only handle left mouse button for long press
    if (!gameOver && e.button === 0) {
      onLongPress();
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    e.preventDefault();
    // Only handle left mouse button clicks
    if (e.button === 0) {
      onLongPressEnd();
      if (!gameOver && !square.flagged) {
        onSquareClick();
      }
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    if (!gameOver) {
      onLongPress();
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    onLongPressEnd();
    if (!gameOver && !square.flagged) {
      onSquareClick();
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent browser context menu
    if (!gameOver) {
      onRightClick(); // Directly call the right-click handler
    }
  };

  return (
    <motion.td
      style={squareStyle}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onContextMenu={handleContextMenu}
      className="game-square"
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.1 }}
    >
      <div style={contentStyle}>
        {getSquareContent()}
      </div>
    </motion.td>
  );
};

export default GameSquare;
